import React from 'react';
import {
  Box,
  Grid,
  Typography,
  CircularProgress,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { TrainingSample } from '../../types';
import { TrainingSampleCard } from './TrainingSampleCard';

interface TrainingSampleGridProps {
  samples: TrainingSample[];
  signedImages: Record<string, any>;
  loadingMore: boolean;
  hasMore: boolean;
  onSampleClick: (sample: TrainingSample) => void;
  onDeleteClick: (sample: TrainingSample, event: React.MouseEvent) => void;
  onRunInference: (sample: TrainingSample, event: React.MouseEvent) => void;
}

export const TrainingSampleGrid: React.FC<TrainingSampleGridProps> = ({
  samples,
  signedImages,
  loadingMore,
  hasMore,
  onSampleClick,
  onDeleteClick,
  onRunInference,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  if (samples.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography variant="h6" color="text.secondary" gutterBottom>
          No training samples found
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Try adjusting your filters or check back later
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Grid container spacing={{ xs: 2, sm: 2, md: 3 }}>
        {samples.map((sample: TrainingSample) => {
          const imageUrl = sample.thumbnail_url || sample.original_image_url;
          const signedImageResult = imageUrl ? signedImages[imageUrl] : null;
          
          return (
            <Grid 
              item 
              xs={12} 
              sm={isTablet ? 12 : 6} 
              md={6}
              lg={6}
              xl={4}
              key={sample.id}
            >
              <TrainingSampleCard
                sample={sample}
                signedImageResult={signedImageResult}
                onView={onSampleClick}
                onDelete={onDeleteClick}
                onRunInference={onRunInference}
              />
            </Grid>
          );
        })}
      </Grid>

      {/* Loading more indicator */}
      {loadingMore && (
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center',
          py: 4,
          gap: 2
        }}>
          <CircularProgress size={24} />
          <Typography variant="body2" color="text.secondary">
            Loading more samples...
          </Typography>
        </Box>
      )}

      {/* End of results indicator */}
      {!hasMore && samples.length > 0 && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="body2" color="text.secondary">
            You've reached the end of the training samples
          </Typography>
        </Box>
      )}
    </>
  );
};